import { useCallback, useState } from 'react';
import { ayeApi } from '../../../core';
import type {
  CardBrand,
  EWalletBrand,
  PaymentMethodBrand
} from '../../../types/registration-payment.types';
import {
  GATEWAY_REQUIRES_IMPORTANT_NOTES,
  PAYMENT_METHOD_CONFIG
} from '../../register/RegisterScreen/constants/payment-method.const';

export type AddPaymentStep =
  | 'select-payment-type'
  | 'select-card-brand'
  | 'select-ewallet-brand';

interface UseAddPaymentFlowOptions {
  phoneNumber: string;
  countryCode: string;
  userId?: string;
  name?: string;
  email?: string;
  isEWalletEnabled: boolean;
}

export interface AddPaymentFlowState {
  step: AddPaymentStep;
  selectedBrand: PaymentMethodBrand | null;
  showImportantNotes: boolean;
  isRedirecting: boolean;
  errorMessage: string | undefined;
}

export interface AddPaymentFlowHandlers {
  step: AddPaymentStep;
  selectedBrand: PaymentMethodBrand | null;
  showImportantNotes: boolean;
  isRedirecting: boolean;
  isResolvingGateway: boolean;
  errorMessage: string | undefined;
  goToTypeSelection: () => void;
  goToCardBrandSelection: () => void;
  goToEWalletBrandSelection: () => void;
  confirmCardBrand: (brand: CardBrand) => void;
  confirmEWalletBrand: (brand: EWalletBrand) => void;
  closeImportantNotes: () => void;
  doAddPayment: () => Promise<void>;
  reset: () => void;
}

const INITIAL_STATE: AddPaymentFlowState = {
  step: 'select-payment-type',
  selectedBrand: null,
  showImportantNotes: false,
  isRedirecting: false,
  errorMessage: undefined
};

export function useAddPaymentFlow({
  phoneNumber,
  countryCode,
  userId,
  name,
  email,
  isEWalletEnabled: _isEWalletEnabled
}: UseAddPaymentFlowOptions): AddPaymentFlowHandlers {
  const [state, setState] = useState<AddPaymentFlowState>(INITIAL_STATE);
  const [isResolvingGateway, setIsResolvingGateway] = useState(false);

  const goToTypeSelection = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: 'select-payment-type',
      selectedBrand: null
    }));
  }, []);

  const goToCardBrandSelection = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'select-card-brand' }));
  }, []);

  const goToEWalletBrandSelection = useCallback(() => {
    setState((prev) => ({ ...prev, step: 'select-ewallet-brand' }));
  }, []);

  const _executePayment = useCallback(
    async (brand: PaymentMethodBrand) => {
      setState((prev) => ({
        ...prev,
        showImportantNotes: false,
        isRedirecting: true,
        errorMessage: undefined
      }));

      try {
        const response = await ayeApi.generatePayUrl({
          body: {
            phoneNumber,
            countryCode,
            userId,
            screen: 'default',
            name: name ?? '',
            email: email ?? '',
            payment_method: brand
          }
        });

        if (response.success) {
          window.open(response.data.payUrl, '_self');
        } else {
          setState((prev) => ({
            ...prev,
            isRedirecting: false,
            errorMessage:
              'Sorry, something went wrong while processing your request. Please try again.'
          }));
        }
      } catch {
        setState((prev) => ({
          ...prev,
          isRedirecting: false,
          errorMessage:
            'Sorry, something went wrong while processing your request. Please try again.'
        }));
      }
    },
    [phoneNumber, countryCode, userId, name, email]
  );

  const confirmBrand = useCallback(
    async (brand: PaymentMethodBrand) => {
      setState((prev) => ({ ...prev, selectedBrand: brand }));
      setIsResolvingGateway(true);

      try {
        const response = await ayeApi.resolveGateway({
          queryParams: { payment_method: brand }
        });
        const requiresNotes =
          GATEWAY_REQUIRES_IMPORTANT_NOTES[
            (response as { data?: { gateway?: string } }).data?.gateway ?? ''
          ] ??
          PAYMENT_METHOD_CONFIG[brand]?.requiresImportantNotes ??
          false;

        setIsResolvingGateway(false);

        if (requiresNotes) {
          setState((prev) => ({ ...prev, showImportantNotes: true }));
        } else {
          await _executePayment(brand);
        }
      } catch {
        // Fallback to static config on API error
        const requiresNotes =
          PAYMENT_METHOD_CONFIG[brand]?.requiresImportantNotes ?? false;

        setIsResolvingGateway(false);

        if (requiresNotes) {
          setState((prev) => ({ ...prev, showImportantNotes: true }));
        } else {
          await _executePayment(brand);
        }
      }
    },
    [_executePayment]
  );

  const confirmCardBrand = useCallback(
    (brand: CardBrand) => confirmBrand(brand),
    [confirmBrand]
  );

  const confirmEWalletBrand = useCallback(
    (brand: EWalletBrand) => confirmBrand(brand),
    [confirmBrand]
  );

  const closeImportantNotes = useCallback(() => {
    setState((prev) => ({ ...prev, showImportantNotes: false }));
  }, []);

  const doAddPayment = useCallback(async () => {
    if (state.selectedBrand) {
      await _executePayment(state.selectedBrand);
    }
  }, [_executePayment, state.selectedBrand]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
    setIsResolvingGateway(false);
  }, []);

  return {
    step: state.step,
    selectedBrand: state.selectedBrand,
    showImportantNotes: state.showImportantNotes,
    isRedirecting: state.isRedirecting,
    isResolvingGateway,
    errorMessage: state.errorMessage,
    goToTypeSelection,
    goToCardBrandSelection,
    goToEWalletBrandSelection,
    confirmCardBrand,
    confirmEWalletBrand,
    closeImportantNotes,
    doAddPayment,
    reset
  };
}
